/**
 * Student Master — compact roster filters + inline search
 */

import { useEffect, useMemo } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import {
  OPERATIONAL_ACADEMIC_YEAR,
  OPERATIONAL_ODD_SEMESTERS,
  filterOperationalSemesters,
} from '../config/operationalAcademicContext';

export function studentMatchesHighlight(student, term) {
  if (!term || !String(term).trim()) return false;
  const t = String(term).trim().toLowerCase();
  const fullName = `${student.first_name || ''} ${student.last_name || ''}`.trim().toLowerCase();
  return (
    fullName.includes(t)
    || String(student.roll_number || '').toLowerCase().includes(t)
    || String(student.email || '').toLowerCase().includes(t)
  );
}

export function programBelongsToDepartment(program, departmentName, departments) {
  if (!departmentName) return true;
  const dept = departments.find(
    (d) => d.name === departmentName
      || d.code === departmentName
      || String(d.id) === String(departmentName)
  );
  if (!dept) return program.department === departmentName;

  if (program.department_id && dept.id && String(program.department_id) === String(dept.id)) {
    return true;
  }
  if (program.department_code && dept.code
    && String(program.department_code).toUpperCase() === String(dept.code).toUpperCase()) {
    return true;
  }
  const linked = departments.find(
    (d) => String(d.id) === String(program.department_id)
      || (program.department_code && d.code && String(d.code).toUpperCase() === String(program.department_code).toUpperCase())
  );
  return linked?.name === departmentName || program.department === departmentName;
}

function divisionAcademicYear(d) {
  return d.academic_year ?? d.academicYear ?? '';
}

function divisionDegreeId(d) {
  return d.degree_id ?? d.degreeId ?? '';
}

export function filterDivisionsForStudentContext(divisions, filters) {
  return (divisions || []).filter((d) => {
    const year = divisionAcademicYear(d);
    if (filters.academicYear && year && year !== filters.academicYear) return false;
    if (filters.degreeId && String(divisionDegreeId(d)) !== String(filters.degreeId)) return false;
    if (filters.semester !== '' && filters.semester != null && Number(d.semester) !== Number(filters.semester)) {
      return false;
    }
    return true;
  });
}

/** Academic years that have at least one division in the catalog (newest first). */
export function academicYearsFromDivisionCatalog(divisions) {
  return Array.from(new Set((divisions || []).map(divisionAcademicYear).filter(Boolean)))
    .sort((a, b) => b.localeCompare(a));
}

export default function StudentMasterFilters({
  searchInput,
  onSearchInputChange,
  onSearchApply,
  onSearchClear,
  highlightTerm,
  studentFilters,
  onFiltersChange,
  departments,
  programs,
  divisions,
  academicYears,
  semesterOptions,
  activeAcademicYear,
  activeSemester,
  canShowRoster,
  rosterShown,
  resultCount,
  highlightMatchCount,
  onShowRoster,
  rosterLoading,
  divisionsCatalogEmpty,
  academicYearLocked = false,
}) {
  const departmentName = studentFilters.department;
  const activePrograms = (programs || []).filter((p) => p.is_active !== false);
  let programOptions = activePrograms.filter((p) => programBelongsToDepartment(p, departmentName, departments));
  if (departmentName && programOptions.length === 0) {
    programOptions = activePrograms;
  }

  const divisionOptions = useMemo(
    () => filterDivisionsForStudentContext(divisions, studentFilters),
    [divisions, studentFilters.academicYear, studentFilters.degreeId, studentFilters.semester]
  );

  const semesterList = useMemo(() => {
    let list = semesterOptions && semesterOptions.length > 0
      ? semesterOptions
      : [...OPERATIONAL_ODD_SEMESTERS];
    list = filterOperationalSemesters(list);
    const program = programs.find((p) => p.id === studentFilters.degreeId);
    const max = Number(program?.number_of_semesters || 8);
    return list.filter((s) => s <= max);
  }, [semesterOptions, programs, studentFilters.degreeId]);

  useEffect(() => {
    if (!studentFilters.semester || divisionOptions.length !== 1) return;
    const onlyId = divisionOptions[0].id;
    if (studentFilters.divisionId !== onlyId) {
      onFiltersChange((f) => ({ ...f, divisionId: onlyId }));
    }
  }, [divisionOptions, studentFilters.semester, studentFilters.divisionId, onFiltersChange]);

  const handleDepartmentChange = (value) => {
    onFiltersChange((f) => ({
      ...f,
      department: value,
      degreeId: '',
      semester: '',
      divisionId: '',
    }));
  };

  const handleProgramChange = (value) => {
    onFiltersChange((f) => ({
      ...f,
      degreeId: value,
      semester: '',
      divisionId: '',
    }));
  };

  const handleAcademicYearChange = (value) => {
    onFiltersChange((f) => ({
      ...f,
      academicYear: value,
      semester: '',
      divisionId: '',
    }));
  };

  const handleSemesterChange = (value) => {
    onFiltersChange((f) => ({
      ...f,
      semester: value === '' ? '' : Number(value),
      divisionId: '',
    }));
  };

  const statusMessage = (() => {
    if (!studentFilters.academicYear || !studentFilters.department || !studentFilters.degreeId) {
      return 'Select academic year, department, and program.';
    }
    if (studentFilters.semester === '' || studentFilters.semester == null) {
      return 'Select semester.';
    }
    if (divisionOptions.length === 0) {
      if (divisionsCatalogEmpty) {
        return 'No divisions in this institute — add them under Academic → Division Master, then return here.';
      }
      return `No divisions for this program/semester in ${OPERATIONAL_ACADEMIC_YEAR} — run roster promotion or seed for the current year.`;
    }
    if (!studentFilters.divisionId) {
      return `Choose a division (${divisionOptions.length} available).`;
    }
    if (!rosterShown) {
      return 'Ready — click Show to load the roster.';
    }
    return `Showing ${resultCount ?? 0} student${resultCount === 1 ? '' : 's'}.`;
  })();

  return (
    <div className="student-roster-panel">
      <div className="student-roster-panel-head">
        <div>
          <h3 className="student-roster-title">Filters</h3>
          {(activeAcademicYear || activeSemester) ? (
            <p className="student-roster-subtitle">
              Open cycle: {activeAcademicYear || '—'}{activeSemester ? ` · Sem ${activeSemester}` : ''}
            </p>
          ) : null}
        </div>
        <div className="student-roster-search" aria-label="Search loaded students">
          <MagnifyingGlassIcon className="student-roster-search-icon" aria-hidden />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => onSearchInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onSearchApply();
              }
            }}
            placeholder="Highlight name, PRN, email…"
            className="input student-roster-search-input"
            aria-label="Search students by name, PRN, or email"
          />
          <button type="button" className="btn-secondary student-roster-search-go" onClick={onSearchApply}>
            Find
          </button>
          {highlightTerm ? (
            <button type="button" className="btn-secondary student-roster-search-go" onClick={onSearchClear}>
              Clear
            </button>
          ) : null}
        </div>
      </div>

      {highlightTerm ? (
        <p className="student-roster-highlight-note">
          {highlightMatchCount} match{highlightMatchCount === 1 ? '' : 'es'} for &ldquo;{highlightTerm}&rdquo;
        </p>
      ) : null}

      <div className="student-roster-filters">
        <div className="student-roster-field">
          <label className="student-roster-label" htmlFor="student-filter-ay">Academic year</label>
          {academicYearLocked ? (
            <div
              id="student-filter-ay"
              className="student-roster-ay-locked"
              title="Prior years are available in Reports only"
            >
              {OPERATIONAL_ACADEMIC_YEAR}
            </div>
          ) : (
            <select
              id="student-filter-ay"
              value={studentFilters.academicYear}
              onChange={(e) => handleAcademicYearChange(e.target.value)}
              className="input student-roster-select"
            >
              <option value="">Year</option>
              {academicYears.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          )}
        </div>
        <div className="student-roster-field">
          <label className="student-roster-label" htmlFor="student-filter-dept">Department</label>
          <select
            id="student-filter-dept"
            value={studentFilters.department}
            onChange={(e) => handleDepartmentChange(e.target.value)}
            className="input student-roster-select"
          >
            <option value="">Department</option>
            {departments.map((d) => (
              <option key={d.id || d.name} value={d.name}>{d.name}</option>
            ))}
          </select>
        </div>
        <div className="student-roster-field">
          <label className="student-roster-label" htmlFor="student-filter-program">Program</label>
          <select
            id="student-filter-program"
            value={studentFilters.degreeId}
            onChange={(e) => handleProgramChange(e.target.value)}
            className="input student-roster-select"
            disabled={!studentFilters.department}
          >
            <option value="">Program</option>
            {programOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.program_title || p.name}
              </option>
            ))}
          </select>
        </div>
        <div className="student-roster-field">
          <label className="student-roster-label" htmlFor="student-filter-sem">Semester</label>
          <select
            id="student-filter-sem"
            value={studentFilters.semester}
            onChange={(e) => handleSemesterChange(e.target.value)}
            className="input student-roster-select"
            disabled={!studentFilters.degreeId}
          >
            <option value="">Sem</option>
            {semesterList.map((s) => (
              <option key={s} value={s}>
                {s}{activeSemester && Number(s) === Number(activeSemester) ? ' ★' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="student-roster-field student-roster-field-division">
          <label className="student-roster-label" htmlFor="student-filter-div">
            Division{divisionOptions.length ? ` (${divisionOptions.length})` : ''}
          </label>
          <select
            id="student-filter-div"
            value={studentFilters.divisionId}
            onChange={(e) => onFiltersChange((f) => ({ ...f, divisionId: e.target.value }))}
            className="input student-roster-select"
            disabled={!studentFilters.semester}
          >
            <option value="">{divisionOptions.length ? 'Division' : '—'}</option>
            {divisionOptions.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} · {d.degree_name || 'Program'} (Sem {d.semester})
              </option>
            ))}
          </select>
        </div>
        <div className="student-roster-field student-roster-field-action">
          <span className="student-roster-label student-roster-label-invisible">Show</span>
          <button
            type="button"
            className="btn-primary student-roster-show-btn"
            disabled={!canShowRoster || rosterLoading}
            onClick={onShowRoster}
          >
            {rosterLoading ? 'Loading…' : 'Show'}
          </button>
        </div>
      </div>

      <p className={`student-roster-status ${canShowRoster ? 'student-roster-status-ready' : ''}`} role="status">
        {statusMessage}
      </p>
    </div>
  );
}
